import React, { useEffect, useMemo, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
  useTheme, useMediaQuery, Menu
} from "@mui/material"
import GridViewIcon from "@mui/icons-material/GridView"
import ViewListIcon from "@mui/icons-material/ViewList"
import { container } from "../../infrastructure/container"
import { SessionService } from "../../infrastructure/services/session"
import { FranchiseService } from "../../infrastructure/services/api/franchise"
import { OrderService } from "../../infrastructure/services/api/orderManagement"
import { GetOrderResponse } from "../../infrastructure/models/transport/response/GetOrdersResponse"
import { Franchise } from "../../infrastructure/models/common/franchise"
import { TYPES } from "../../presentation/models/types"
import { COLORS } from "../../presentation/constants/colors"
import TerminalForm from "./TerminalForm"
import Searchbar from "./Searchbar"
import { useSearch } from "../../presentation/context/SearchContext"
import { Terminal } from "../../infrastructure/models/terminal"
import { Logger } from "../../infrastructure/services/logger"
import { SupportRole } from "../../infrastructure/models/common/supportrole"
import lockerIcon from "../../asset/locker_icon.png"
import LogoutButton from "./LogoutButton"
import { MenuIcon, Minus, Plus } from "lucide-react"
import DeleteIcon from '@mui/icons-material/Delete'
import FranchiseUsers from "./FranchiseUsers"
import FranchiseOrders from "./FranchiseOrders"
import MoreVertIcon from "@mui/icons-material/MoreVert"

type DateRange = [Date | null, Date | null]

function formatToIST(date: Date | string | null | undefined, options: Intl.DateTimeFormatOptions = {}): string {
  if (!date) return "-" // Or return an empty string, or "Invalid Date"

  const d = new Date(date)
  if (isNaN(d.getTime())) return "-" // Catch "Invalid Date"

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    ...options,
  }).format(d)
}

function formatRange(start: Date, end: Date): string {
  return `${formatToIST(start, { day: "2-digit", month: "short" })} - ${formatToIST(end, { day: "2-digit", month: "short" })}`
}

function convertToIST(date: Date | null): Date | null {
  if (!date) return null
  const utc = date.getTime() + date.getTimezoneOffset() * 60000
  const ISTOffset = 5.5 * 60 * 60000
  return new Date(utc + ISTOffset)
}

const FranchiseDetails: React.FC = () => {
  const { franchiseId } = useParams()
  const sessionService = container.resolve<SessionService>("sessionService")
  const franchiseService = container.resolve<FranchiseService>("franchiseService")
  const orderService = container.resolve<OrderService>("orderService")
  const navigate = useNavigate()
  const { searchQuery } = useSearch()
  const [franchise, setFranchise] = useState<Franchise | null>(null)
  const [terminals, setTerminals] = useState<any[]>([])
  const [orders, setOrders] = useState<GetOrderResponse[]>([])
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [apiLoading, setApiLoading] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [cardView, setCardView] = useState(true)
  const logger = container.resolve<Logger>("logger")

  // Store session data in local state to prevent unnecessary re-renders
  const [franchises, setFranchises] = useState(sessionService.franchises)
  const [allTerminals, setAllTerminals] = useState(sessionService.terminals)
  const [selectedTerminalId, setSelectedTerminalId] = useState<string>("")
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedOrder, setSelectedOrder] = useState<GetOrderResponse | null>(null)

  const [rangeStart, setRangeStart] = useState<Date | null>(null)
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null)
  const [rangeValue, setRangeValue] = useState<DateRange>([null, null])
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [terminalToDelete, setTerminalToDelete] = useState<Terminal | null>(null)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [displayedTerminals, setDisplayedTerminals] = useState<Terminal[]>(sessionService.terminals)
  const currentUser = sessionService.user
  const isSuperAdmin = currentUser?.role === SupportRole.SUPERADMIN
  const today = convertToIST(new Date())!
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const [expandedDate, setExpandedDate] = useState<string | null>(null)
  const [expandedAll, setExpandedAll] = useState<boolean>(false)
  const [apiError, setApiError] = useState<{ code?: string, message?: string } | null>(null)

  const toggleTableExpand = (date: string) => {
    setExpandedDate(prev => (prev === date ? null : date))
  }

  const [menuEnabled, setMenuEnabled] = useState(false)
  const [tooltipText, setTooltipText] = useState("More actions")

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!menuEnabled) {
      // First click: enable the menu, but don’t open yet
      setMenuEnabled(true)
      setTooltipText("Click again to see dropdown")
      return
    }
    // From second click onwards → open menu
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setTooltipText("More actions") // reset tooltip
    setMenuEnabled(false) // reset flow
  }

  const normalizeStatus = (status: string): string => status.trim().toLowerCase().replaceAll("_", "-")

  const [sortConfig, setSortConfig] = useState<{ key: string, direction: "asc" | "desc" } | null>(null)
  const handleSort = (key: string) => {
    setSortConfig(prev => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" }
      }
      return { key, direction: "asc" }
    })
  }

  const sizePriority: Record<string, number> = {
    small: 1,
    medium: 2,
    large: 3,
    x_l: 4,
    custom: 5
  }

  const statusPriority1: Record<string, number> = {
    "in-progress": 1,
    "completed": 2,
    "pending": 3,
    "pickup-locker-pending": 4,
    "canceled": 5
  }
  const [deleteFranchiseConfirmOpen, setDeleteFranchiseConfirmOpen] = useState(false)

  useEffect(() => {
    if (rangeStart && rangeEnd) {
      setRangeValue([rangeStart, rangeEnd])
    } else {
      setRangeValue([null, null])
    }
  }, [rangeStart, rangeEnd])

  // Sync session data when it changes
  useEffect(() => {
    setFranchises(sessionService.franchises)
    setAllTerminals(sessionService.terminals)
  }, [])

  // Fetch franchise and related terminals when franchiseId changes
  useEffect(() => {
    if (!franchiseId) return
    const currentFranchise = sessionService.currentFranchise
    if (franchiseId) {
      setFranchise(currentFranchise)
      const franchiseTerminals = allTerminals.filter(
        (t: any) => t.franchiseId === franchiseId
      )
      setTerminals(franchiseTerminals)
    } else {
      setError("Franchise not found.")
    }
  }, [franchiseId, franchises, allTerminals])

  /**
   * Handles form submission for creating a new terminal.
   * @param data - Terminal data to be created.
   */
  const handleSubmit = async (data: any) => {
    setApiLoading(true)
    try {
      const createdTerminal = await franchiseService.createTerminal(data)
      if (createdTerminal) {
        // Update session storage
        sessionService.addTerminal(createdTerminal)

        // Directly update local terminals state
        setTerminals((prev) => [...prev, createdTerminal])

        // Also update franchise.terminals if needed for filtering
        setFranchise((prev) =>
          prev
            ? {
              ...prev,
              terminals: [...(prev.terminals || []), createdTerminal],
            }
            : prev
        )

        setOpen(false)
      }
    } catch (error) {
      setError("Failed to create terminal. Please try again.")
      const apiData = error?.response?.data || error

      if (apiData?.code === "MF01") {
        setApiError(apiData)
        return
      }

      setError(apiData?.message || "Something went wrong")
    } finally {
      setApiLoading(false)
    }
  }

  const formatAddress = (address: any): string => {
    if (!address) return 'N/A'

    if (typeof address === 'string') return address

    const { street = '', city = '', state = '', postalCode = '' } = address
    return `${street}, ${city}, ${state} - ${postalCode}`
  }

  /* searchbar filteration*/
  const filteredTerminals = franchise?.terminals?.filter((terminal: any) => {
    const addressString =
      typeof terminal.address === 'string'
        ? terminal.address
        : `${terminal.address?.street || ''} ${terminal.address?.city || ''} ${terminal.address?.state || ''} ${terminal.address?.postalCode || ''}`

    return (
      terminal.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      addressString.toLowerCase().includes(searchQuery.toLowerCase()) ||
      terminal.terminalType.toLowerCase().includes(searchQuery.toLowerCase()) ||
      terminal.status.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }) || []

  /* calender, searchbar and select dropdown filteration*/
  const filteredOrders = orders.filter(order => {
    const terminalName = (franchise?.terminals as any[])?.find((t: any) => t.terminalId === order.terminal)?.name || ""
    const orderDateObj = order.dropOffTime ? new Date(order.dropOffTime) : null
    const orderDateStr = orderDateObj?.toISOString().split("T")[0] || null

    const matchesSearch =
      terminalName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.status?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.receiverMobile?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.lockerSize?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesDate =
      !selectedDate ||
      (orderDateStr &&
        new Date(orderDateStr).toDateString() === selectedDate.toDateString())

    const matchesTerminal = selectedTerminalId
      ? order.terminal === selectedTerminalId
      : true

    const fromDate = rangeValue[0]
      ? new Date(new Date(rangeValue[0]).setHours(0, 0, 0, 0))
      : null

    const toDate = rangeValue[1]
      ? new Date(new Date(rangeValue[1]).setHours(23, 59, 59, 999))
      : null
    const matchesRangeDate =
      (!fromDate || !toDate) ||
      (orderDateObj && fromDate && toDate &&
        orderDateObj >= fromDate && orderDateObj <= toDate)

    return matchesSearch && matchesDate && matchesTerminal && matchesRangeDate
  })

  const grouped = filteredOrders.reduce((acc, order) => {
    const key = new Date(order.dropOffTime).toISOString().split("T")[0]
    if (!acc[key]) acc[key] = []
    acc[key].push(order)
    return acc
  }, {} as Record<string, GetOrderResponse[]>)

  const dates = Object.keys(grouped).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime()
  )

  const fromDate = rangeValue[0]
    ? new Date(new Date(rangeValue[0]).setHours(0, 0, 0, 0))
    : null

  const toDate = rangeValue[1]
    ? new Date(new Date(rangeValue[1]).setHours(23, 59, 59, 999))
    : null

  const rangeTotal = orders
    .filter(order => {
      const dropTime = new Date(order.dropOffTime)
      return (
        fromDate &&
        toDate &&
        dropTime >= fromDate &&
        dropTime <= toDate
      )
    })
    .flatMap(order =>
      order.payments?.filter(p => p.status.toLowerCase() === "approved") || []
    )
    .reduce((sum, p) => sum + p.amount, 0)

  const dateWiseTotals: Record<string, number> = {}

  dates.forEach((dateStr) => {
    const paymentsForDate = grouped[dateStr].flatMap((order) =>
      order.payments?.filter((p) => p.status.toLowerCase() === 'approved') || []
    )
    dateWiseTotals[dateStr] = paymentsForDate.reduce((sum, p) => sum + p.amount, 0)
  })

  useEffect(() => {
    setFranchise(sessionService.currentFranchise)
    setTerminals(sessionService.terminals.filter(t => t.franchiseId === franchiseId))
  }, [franchiseId, sessionService])

  const totalOrders = orders.length

  const totalRevenue = filteredOrders.reduce((sum, order) => {
    const paymentSum = order.payments
      ?.filter(p => p.status.toLowerCase() === "approved")
      .reduce((acc, p) => acc + Number(p.amount), 0) || 0
    return sum + paymentSum
  }, 0)

  const handleTerminalClick = (terminalId: string) => {
    navigate(`/terminals/${terminalId}`)
  }

  useEffect(() => {
    if (successModalOpen) {
      const timer = setTimeout(() => {
        setSuccessModalOpen(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [successModalOpen])

  const handleConfirmDeleteFranchise = async () => {
    if (!franchiseId) return

    setApiLoading(true)
    try {
      await franchiseService.deleteFranchise(franchiseId)

      // Remove from full franchises
      sessionService.setFranchises(
        sessionService.franchises.filter(f => f.franchiseId !== franchiseId)
      )

      // Remove from brief franchises
      sessionService.setFranchisesBrief(
        sessionService.franchiseBrief.filter(f => f.franchiseId !== franchiseId)
      )

      navigate("/dashboard")

    } catch (error) {
      logger.error("Failed to delete franchise", error)
      alert("Failed to delete franchise. Please try again.")
    } finally {
      setApiLoading(false)
      setDeleteFranchiseConfirmOpen(false)
    }
  }

  const [confirmStage, setConfirmStage] = useState(false)

  const handleDeleteClick = () => {
    if (!confirmStage) {
      // First click just enables confirm mode
      setConfirmStage(true)
    } else {
      setDeleteFranchiseConfirmOpen(true)
    }
  }

  return (
    <Box p={3}>
      <AppBar position="fixed" sx={{ backgroundColor: COLORS.SIDEBAR_BG, boxShadow: 1 }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between", px: 2 }}>
          {/* Logo */}
          <Box display="flex" alignItems="center" sx={{ backgroundColor: COLORS.WHITE, boxShadow: 1, borderRadius: "8px" }}>
            <img src={lockerIcon} alt="Locker Logo" style={{ height: 40, marginRight: 16, }} />
          </Box>

          {isMobile ? (
            <>
              <IconButton onClick={handleMenuOpen} sx={{ color: COLORS.WHITE }}>
                <MenuIcon />
              </IconButton>

              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>

                <MenuItem
                  onClick={() => {
                    setTabIndex(0)
                    handleMenuClose()
                  }}
                >
                  Terminals
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setTabIndex(1)
                    handleMenuClose()
                  }}
                >
                  Franchise Users
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setTabIndex(2)
                    handleMenuClose()
                  }}
                >
                  Orders
                </MenuItem>

                <MenuItem onClick={handleMenuClose}>
                  <LogoutButton className="w-full text-red-500 font-medium" />
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Tabs
                value={tabIndex}
                onChange={(_, newValue) => setTabIndex(newValue)}
                textColor="inherit"
                sx={{
                  flexGrow: 1,
                  ".MuiTab-root": {
                    color: COLORS.WHITE,
                    fontWeight: 500,
                    opacity: 1,
                  },
                  ".Mui-selected": {
                    color: COLORS.WHITE,
                  },
                  ".MuiTabs-indicator": {
                    backgroundColor: COLORS.WHITE,
                  },
                }}
                centered
              >
                <Tab label="Terminals" />
                <Tab label="Users" />
                <Tab label="Orders" />
              </Tabs>

              <Box>
                <LogoutButton className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-all duration-300" />
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box
        display="flex" justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}
        flexDirection={{ xs: 'column', md: 'row' }} mb={2} gap={1} paddingTop={5}
      >
        <Tabs value={tabIndex} onChange={(_, val) => setTabIndex(val)}>
          {/* <Tab label="Terminals" />
          <Tab label="Orders" /> */}
        </Tabs>

        <Box textAlign={{ xs: 'left', md: 'right' }} width={{ xs: '100%' }} fontSize={{ xs: '10px' }}>
          <Typography variant="body2" sx={{ fontSize: { xs: '12px', sm: '16px' }, color: 'black' }}>
            Owner: {franchise?.owner.firstname} {franchise?.owner.lastname}, &nbsp;&nbsp;
            Phone Number: {franchise?.owner.phone.primary}
          </Typography>
        </Box>
      </Box>

      {tabIndex === 0 && franchise && (
        <>
          <Box
            className="mb-3"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 3,
            }}
          >

            {/* LEFT COLUMN: Franchise Info */}
            <Box sx={{ flex: 1, minWidth: 250 }}>

              <Typography variant="h6" fontWeight="bold">
                Franchise: {franchise.name}
              </Typography>

              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Address: {formatAddress(franchise.address)}
              </Typography>

              <Box
                className="px-3 p-2 text-xs font-medium rounded-full text-white mt-1"
                sx={{
                  display: 'inline-block',
                  backgroundColor:
                    franchise.status.toLowerCase() === TYPES.ACTIVE
                      ? COLORS.ACTIVE
                      : franchise.status.toLowerCase() === TYPES.INACTIVE
                        ? COLORS.INACTIVE
                        : COLORS.MAINTENANCE,
                }}
              >
                {franchise.status}
              </Box>
            </Box>

            {/* RIGHT COLUMN: Stats + Searchbar + Buttons */}
            <Box
              sx={{
                flex: 1,
                minWidth: 300,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                alignItems: { xs: 'flex-start', sm: 'flex-end' },
              }}
            >

              {/* Searchbar + Buttons in one row */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Searchbar />

                <Button variant="contained" color="secondary" onClick={() => navigate(-1)}>
                  Back
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpen(true)}
                  disabled={apiLoading}
                >
                  Create Terminal
                </Button>

                <Tooltip title={tooltipText} arrow>
                  <IconButton
                    onClick={handleMenuOpen}
                    sx={{
                      backgroundColor: COLORS.ASH,
                      borderRadius: "50%",
                      "&:hover": {
                        backgroundColor: COLORS.ASH,
                        opacity: 0.9,
                      },
                    }}
                  >
                    <MoreVertIcon sx={{ color: COLORS.BLACK }} />
                  </IconButton>
                </Tooltip>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  {isSuperAdmin && (
                    <MenuItem onClick={handleDeleteClick}>
                      <DeleteIcon sx={{ mr: 1, color: "error.main" }} />
                      {confirmStage ? "Click Again to Confirm" : "Delete Franchise"}
                    </MenuItem>
                  )}
                </Menu>

              </Box>
            </Box>
          </Box>

          {error ? (
            <p className="text-red-600">{error}</p>
          ) : (
            <>
              <Card sx={{ p: 0, boxShadow: 3, maxWidth: "100%", width: "100%" }}>
                <CardContent>
                  <Box
                    className="mb-3"
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <Typography variant="body1" fontWeight="medium">
                        Franchise ID: {franchise.franchiseId}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body1" fontWeight="medium">
                        Terminals: {franchise.terminals.length}
                      </Typography>
                      <IconButton
                        onClick={() => setCardView(prev => !prev)}
                        sx={{
                          border: '1px solid #ccc',
                          borderRadius: 2,
                          color: COLORS.NAVY_BLUE,
                        }}
                      >
                        {cardView ? <ViewListIcon /> : <GridViewIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  {filteredTerminals.length === 0 ? (
                    <Typography variant="body2" className="text-gray-600">
                      No Terminals Available.
                    </Typography>
                  ) : cardView ? (
                    <Box sx={{ mt: 2 }}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {filteredTerminals.map((terminal: any, index: number) => (
                          <Box
                            key={index}
                            className="bg-white p-4 rounded-lg shadow-md text-left cursor-pointer hover:shadow-lg transition border border-gray-200 flex flex-col h-full relative"
                            onClick={() => handleTerminalClick(terminal.terminalId)}
                          >
                            <div className="flex-grow">
                              <Typography variant="subtitle1" fontWeight="800" className="mb-2">
                                {`Terminal - ${terminal.name}`}
                              </Typography>

                              <Typography variant="body2" className="text-gray-700">
                                Address: {formatAddress(terminal.address)}
                              </Typography>

                              <Typography variant="body2" className="text-gray-700">
                                Type: {terminal.terminalType}
                              </Typography>

                              {terminal.enrolledDevice && terminal.enrolledDevice.status !== "Unenrolled" ? (
                                <Box sx={{ mt: 1, pl: 1, borderLeft: '3px solid #00ff00' }}>
                                  <Typography variant="body2" className="text-gray-700">
                                    <strong>Device:</strong> {terminal.enrolledDevice.deviceMake} {terminal.enrolledDevice.deviceModel}
                                  </Typography>
                                  <Typography variant="body2" className="text-gray-700">
                                    <strong>OS:</strong> {terminal.enrolledDevice.osVersion}
                                  </Typography>
                                  <Typography variant="body2" className="text-gray-700">
                                    <strong>App:</strong> {terminal.enrolledDevice.appVersion}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={{ mt: 1, pl: 1, borderLeft: '3px solid #EC5800' }}>
                                  <Typography variant="body2" className="mt-1 text-gray-700">
                                    <span
                                      className="inline-block px-3 py-1 mt-2 text-xs font-semibold rounded-full text-white"
                                      style={{
                                        backgroundColor: COLORS.LIGHT_ORANGE,
                                      }}
                                    >
                                      No device enrolled
                                    </span>
                                  </Typography>
                                </Box>
                              )}
                            </div>

                            {/* Bottom section (always aligned) */}
                            <div className="mt-auto pt-2">
                              <span
                                className="inline-block px-3 py-1 text-xs font-semibold rounded-full text-white"
                                style={{
                                  backgroundColor:
                                    terminal.status.toLowerCase() === TYPES.ACTIVE
                                      ? COLORS.ACTIVE
                                      : terminal.status.toLowerCase() === TYPES.INACTIVE
                                        ? COLORS.INACTIVE
                                        : COLORS.MAINTENANCE,
                                }}
                              >
                                {terminal.status}
                              </span>
                            </div>
                          </Box>
                        ))}
                      </div>
                    </Box>

                  ) : (
                    <Box sx={{ mt: 2 }} >
                      <div className="w-full overflow-x-auto">
                        <table className="w-full min-w-[600px] table-auto border border-gray-300 text-sm table-responsive">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left font-semibold text-gray-700">#</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-700">Name</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-700">Address</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-700">Type</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-700">Device Enrolled</th>
                              <th className="px-4 py-2 text-left font-semibold text-gray-700">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredTerminals.map((terminal: any, index: number) => (
                              <tr key={index} className="border-t cursor-pointer" onClick={() => handleTerminalClick(terminal.terminalId)}>
                                <td className="px-4 py-2">{index + 1}</td>
                                <td className="px-4 py-2">{terminal.name}</td>
                                <td className="px-4 py-2">{formatAddress(terminal.address)}</td>
                                <td className="px-4 py-2">{terminal.terminalType}</td>
                                <td className="px-4 py-2">
                                  {terminal.enrolledDevice && terminal.enrolledDevice.status !== "Unenrolled" ? (
                                    <div className="text-gray-700 text-sm leading-tight space-y-1">
                                      <div>
                                        <strong>Device:</strong> {terminal.enrolledDevice.deviceMake} {terminal.enrolledDevice.deviceModel}
                                      </div>
                                      <div>
                                        <strong>OS:</strong> {terminal.enrolledDevice.osVersion}
                                      </div>
                                      <div>
                                        <strong>App:</strong> {terminal.enrolledDevice.appVersion}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex items-center">
                                      <span
                                        className="inline-block px-3 py-1 text-xs font-semibold rounded-full text-white"
                                        style={{ backgroundColor: COLORS.INACTIVE }}
                                      >
                                        No device enrolled
                                      </span>
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-2">
                                  <span
                                    className="inline-block px-3 py-1 text-xs font-semibold rounded-full text-white"
                                    style={{
                                      backgroundColor:
                                        terminal.status.toLowerCase() === TYPES.ACTIVE
                                          ? COLORS.ACTIVE
                                          : terminal.status.toLowerCase() === TYPES.INACTIVE
                                            ? COLORS.INACTIVE
                                            : COLORS.MAINTENANCE,
                                    }}
                                  >
                                    {terminal.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>Create New Terminal</DialogTitle>
            <DialogContent>
              {apiLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress />
                </Box>
              ) : (
                <TerminalForm onSubmit={handleSubmit} initialValues={{ franchiseId }} apiError={apiError} />
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setOpen(false)} color="secondary" disabled={apiLoading}>
                Cancel
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {tabIndex === 2 && (
        <>
          <FranchiseOrders />
        </>
      )}

      {tabIndex === 1 && (
        <>
          <FranchiseUsers franchiseId={franchise.franchiseId}
            terminals={franchise.terminals ?? []}
          />
        </>
      )}

      <Dialog
        open={deleteFranchiseConfirmOpen}
        onClose={() => setDeleteFranchiseConfirmOpen(false)}
      >
        <DialogTitle>Delete Franchise</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the franchise "{franchise?.name}".
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFranchiseConfirmOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteFranchise}
            color="error"
            disabled={apiLoading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  )
}

export default FranchiseDetails